import { AuthProvider } from "../values";
import { microsoftAuthService, msalInstance } from "./config";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

export const fetchEmails = async (account, setLoading, setEmails, setError, folder = 'inbox') => {
    try {
        if (!account) {
            throw new Error("Microsoft account is undefined or null");
        }
        setLoading(true);
        setEmails([]);
        let tokenResponse;
        try {
            if (typeof account === 'string') {
                tokenResponse = await microsoftAuthService.getAccessToken(
                    account,
                    ['Mail.Read', 'Mail.ReadWrite', 'User.Read'],
                );
            } else if (account.id) {
                tokenResponse = await microsoftAuthService.getAccessToken(
                    account.id,
                    ['Mail.Read', 'Mail.ReadWrite', 'User.Read'],
                );
            } else {
                tokenResponse = await msalInstance.acquireTokenSilent({
                    scopes: ['Mail.Read', 'Mail.ReadWrite', 'User.Read'],
                    account: account
                });
            }
        } catch (tokenError) {
            if (tokenError.message && tokenError.message.includes("MSAL account not found")) {
                throw new Error("Your Microsoft session has expired. Please log out and log in again to refresh your connection.");
            }
            throw tokenError;
        }

        const accessToken = typeof tokenResponse === 'string'
            ? tokenResponse
            : tokenResponse.accessToken;
        let folderEndpoint = 'messages';
        switch (folder) {
            case 'inbox':
                folderEndpoint = 'mailFolders/inbox/messages';
                break;
            case 'junk':
                folderEndpoint = 'mailFolders/junkemail/messages';
                break;
            case 'sent':
                folderEndpoint = 'mailFolders/sentitems/messages';
                break;
            case 'drafts':
                folderEndpoint = 'mailFolders/drafts/messages';
                break;
            case 'archive':
                folderEndpoint = 'mailFolders/archive/messages';
                break;
            case 'trash':
                folderEndpoint = 'mailFolders/deleteditems/messages';
                break;
            default:
                folderEndpoint = 'messages';
                break;
        }

        const response = await fetch(`https://graph.microsoft.com/v1.0/me/${folderEndpoint}?$top=75&$select=subject,body,bodyPreview,from,receivedDateTime,isRead&$expand=attachments`, {
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                'Prefer': 'outlook.body-content-type="html"',
            }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(`Failed to fetch emails: ${response.status} - ${errorData.error?.message || response.statusText}`);
        }

        const data = await response.json();
        const transformedEmails = data.value.map(email => {
            const attachments = email.attachments?.map(att => ({
                id: att.id,
                name: att.name,
                size: att.size,
                isInline: att.isInline,
                contentType: att.contentType,
            })) || null;

            return {
                fullEmail: email,
                read: email.isRead,
                attachments: attachments,
                subject: email.subject || '',
                body: email.body?.content || '',
                message: email.bodyPreview || '',
                name: email.from?.emailAddress?.name || 'Unknown Sender',
                date: new Date(email.receivedDateTime).toLocaleDateString(),
                to: email.toRecipients?.map(r => r.emailAddress?.address).join(', ') || '',
                time: new Date(email.receivedDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                imageUrl: email.imageUrl ?? 'https://static.vecteezy.com/system/resources/previews/026/196/789/non_2x/profile-icon-symbol-design-illustration-vector.jpg',
                account: account,
                provider: AuthProvider.MICROSOFT,
                folder: folder,
            };
        });
        setEmails(transformedEmails);
    } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
            try {
                let tokenResponse;
                if (typeof account === 'string') {
                    try {
                        tokenResponse = await microsoftAuthService.getAccessToken(
                            account,
                            ['Mail.Read', 'Mail.ReadWrite', 'User.Read'],
                        );
                    } catch (serviceError) {
                        tokenResponse = await msalInstance.acquireTokenPopup({
                            scopes: ['Mail.Read', 'Mail.ReadWrite', 'User.Read'],
                            account: msalInstance.getActiveAccount(),
                        });
                    }
                } else if (account.id) {
                    try {
                        tokenResponse = await microsoftAuthService.getAccessToken(
                            account.id,
                            ['Mail.Read', 'Mail.ReadWrite', 'User.Read'],
                        );
                    } catch (serviceError) {
                        tokenResponse = await msalInstance.acquireTokenPopup({
                            scopes: ['Mail.Read', 'Mail.ReadWrite', 'User.Read'],
                            account: msalInstance.getActiveAccount(),
                        });
                    }
                } else {
                    tokenResponse = await msalInstance.acquireTokenPopup({
                        scopes: ['Mail.Read', 'Mail.ReadWrite', 'User.Read'],
                        account: account,
                    });
                }

                const accessToken = typeof tokenResponse === 'string'
                    ? tokenResponse
                    : tokenResponse.accessToken;
                let folderEndpoint = 'messages';
                switch (folder) {
                    case 'inbox':
                        folderEndpoint = 'mailFolders/inbox/messages';
                        break;
                    case 'junk':
                        folderEndpoint = 'mailFolders/junkemail/messages';
                        break;
                    case 'sent':
                        folderEndpoint = 'mailFolders/sentitems/messages';
                        break;
                    case 'drafts':
                        folderEndpoint = 'mailFolders/drafts/messages';
                        break;
                    case 'archive':
                        folderEndpoint = 'mailFolders/archive/messages';
                        break;
                    case 'trash':
                        folderEndpoint = 'mailFolders/deleteditems/messages';
                        break;
                    default:
                        folderEndpoint = 'messages';
                        break;
                }
                const response = await fetch(`https://graph.microsoft.com/v1.0/me/${folderEndpoint}?$top=75&$select=subject,body,bodyPreview,from,receivedDateTime,isRead&$expand=attachments`, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                        'Prefer': 'outlook.body-content-type="html"',
                    }
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(`Failed to fetch emails: ${response.status} - ${errorData.error?.message || response.statusText}`);
                }
                const data = await response.json();
                const transformedEmails = data.value.map(email => {
                    const attachments = email.attachments?.map(att => ({
                        id: att.id,
                        name: att.name,
                        contentType: att.contentType,
                        size: att.size,
                        isInline: att.isInline,
                    })) || null;

                    return {
                        fullEmail: email,
                        read: email.isRead,
                        attachments: attachments,
                        subject: email.subject || '',
                        body: email.body?.content || '',
                        message: email.bodyPreview || '',
                        name: email.from?.emailAddress?.name || 'Unknown Sender',
                        date: new Date(email.receivedDateTime).toLocaleDateString(),
                        to: email.toRecipients?.map(r => r.emailAddress?.address).join(', ') || '',
                        time: new Date(email.receivedDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        imageUrl: email.imageUrl ?? 'https://static.vecteezy.com/system/resources/previews/026/196/789/non_2x/profile-icon-symbol-design-illustration-vector.jpg',
                        account: account,
                        provider: AuthProvider.MICROSOFT,
                        folder: folder,
                    };
                });
                setEmails(transformedEmails);
            } catch (popupErr) {
                setError(`Failed to refresh access: ${popupErr.message}`);
            }
        } else {
            setError(`Failed to fetch Microsoft emails: ${err.message}`);
        }
    } finally {
        setLoading(false);
    }
};

export const getMicrosoftAttachmentContent = async (account, messageId, attachmentId) => {
    try {
        let tokenResponse;
        if (typeof account === 'string') {
            tokenResponse = await microsoftAuthService.getAccessToken(
                account,
                ['Mail.Read', 'Mail.ReadWrite'],
            );
        } else if (account.id) {
            tokenResponse = await microsoftAuthService.getAccessToken(
                account.id,
                ['Mail.Read', 'Mail.ReadWrite'],
            );
        } else {
            tokenResponse = await msalInstance.acquireTokenSilent({
                scopes: ['Mail.Read', 'Mail.ReadWrite'],
                account: account
            });
        }
        const accessToken = typeof tokenResponse === 'string'
            ? tokenResponse
            : tokenResponse.accessToken;
        const metadataResponse = await fetch(`https://graph.microsoft.com/v1.0/me/messages/${messageId}/attachments/${attachmentId}`, {
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Accept': 'application/json',
            }
        });

        if (!metadataResponse.ok) {
            throw new Error(`Failed to fetch attachment metadata: ${metadataResponse.status} ${metadataResponse.statusText}`);
        }

        const metadata = await metadataResponse.json();
        const contentType = metadata.contentType || 'application/octet-stream';
        const response = await fetch(`https://graph.microsoft.com/v1.0/me/messages/${messageId}/attachments/${attachmentId}/$value`, {
            headers: {
                'Authorization': `Bearer ${accessToken}`,
            }
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch attachment: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                resolve({
                    contentUrl: reader.result,
                    contentType: contentType,
                    size: blob.size,
                    isDataUrl: true,
                });
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        throw error;
    }
};

export const sendMicrosoftReply = async (account, emailId, replyContent, setLoading, setError) => {
    try {
        setLoading(true);
        let accessToken;
        try {
            const requiredScopes = ['Mail.Send', 'Mail.ReadWrite'];
            if (typeof account === 'string') {
                accessToken = await microsoftAuthService.getAccessToken(account, requiredScopes);
            } else if (account.id) {
                accessToken = await microsoftAuthService.getAccessToken(account.id, requiredScopes);
            } else {
                const tokenResponse = await msalInstance.acquireTokenSilent({
                    scopes: requiredScopes,
                    account: account
                });
                accessToken = tokenResponse.accessToken;
            }
        } catch (tokenError) {
            if (tokenError instanceof InteractionRequiredAuthError) {
                const requiredScopes = ['Mail.Send', 'Mail.ReadWrite'];
                if (typeof account === 'string' || account.id) {
                    accessToken = await microsoftAuthService.getAccessToken(
                        typeof account === 'string' ? account : account.id,
                        requiredScopes,
                        true,
                    );
                } else {
                    const tokenResponse = await msalInstance.acquireTokenPopup({
                        scopes: requiredScopes,
                        account: account,
                    });
                    accessToken = tokenResponse.accessToken;
                }
            } else {
                throw tokenError;
            }
        }
        const getMessageResponse = await fetch(`https://graph.microsoft.com/v1.0/me/messages/${emailId}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
            }
        });
        if (!getMessageResponse.ok) {
            const errorData = await getMessageResponse.json().catch(() => ({}));
            throw new Error(`Failed to fetch original message: ${getMessageResponse.status} - ${errorData.error?.message || getMessageResponse.statusText}`);
        }
        //const originalMessage = await getMessageResponse.json();
        const replyPayload = {
            comment: replyContent,
        };
        const response = await fetch(`https://graph.microsoft.com/v1.0/me/messages/${emailId}/reply`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify(replyPayload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            if (errorData.error) {
                if (errorData.error.code === "ErrorSendAsDenied") {
                    throw new Error("You don't have permission to send mail on behalf of this account. Please check mailbox permissions.");
                } else if (errorData.error.code === "AccessDenied" || errorData.error.code === "AuthenticationFailed") {
                    throw new Error("Authentication failed. Please log out and log in again.");
                }
            }

            throw new Error(`Failed to send reply: ${response.status} - ${errorData.error?.message || response.statusText}`);
        }

        return true;
    } catch (err) {
        setError(`Failed to send reply: ${err.message}`);
        return false;
    } finally {
        setLoading(false);
    }
};

export const deleteEmail = async (account, emailId, setLoading, setEmails, setError) => {
    try {
        setLoading(true);
        let accessToken;
        try {
            if (typeof account === 'string') {
                accessToken = await microsoftAuthService.getAccessToken(account, ['Mail.ReadWrite']);
            } else if (account.id) {
                accessToken = await microsoftAuthService.getAccessToken(account.id, ['Mail.ReadWrite']);
            } else {
                const tokenResponse = await msalInstance.acquireTokenSilent({
                    scopes: ['Mail.ReadWrite'],
                    account: account,
                });
                accessToken = tokenResponse.accessToken;
            }
        } catch (tokenError) {
            if (tokenError instanceof InteractionRequiredAuthError) {
                if (typeof account === 'string' || account.id) {
                    accessToken = await microsoftAuthService.getAccessToken(
                        typeof account === 'string' ? account : account.id,
                        ['Mail.ReadWrite']
                    );
                } else {
                    const tokenResponse = await msalInstance.acquireTokenPopup({
                        scopes: ['Mail.ReadWrite'],
                        account: account
                    });
                    accessToken = tokenResponse.accessToken;
                }
            } else {
                throw tokenError;
            }
        }
        const response = await fetch(`https://graph.microsoft.com/v1.0/me/messages/${emailId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${accessToken}` }
        });

        if (!response.ok) {
            const errorData = await response.text().catch(() => '');
            throw new Error(`Failed to delete email: ${response.status} ${errorData}`);
        }
        setEmails(prevEmails => prevEmails.filter(email => email.fullEmail.id !== emailId));
        return true;
    } catch (err) {
        setError(`Failed to delete email: ${err.message}`);
        return false;
    } finally {
        setLoading(false);
    }
};